import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  Plus, Minus, Trash2, Search, X, DollarSign, CreditCard, 
  Banknote, Check, Edit, Save, Coffee, UtensilsCrossed, Camera,
  Users, User, UserPlus, Printer, FileText, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import { escapeHtml } from '../../utils/security';
import BarcodeScanner from '../BarcodeScanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  libre: { bg: '#dcfce7', border: '#86efac', text: '#166534', label: 'Libre' },
  ocupada: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', label: 'Ocupada' },
  por_cobrar: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', label: 'Por cobrar' }
};

const PERSON_COLORS = ['#3b82f6','#ef4444','#10b981','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#f97316'];

const WAITER_COLORS = [
  { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
  { bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d' },
  { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
  { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  { bg: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
  { bg: '#ffedd5', border: '#fdba74', text: '#9a3412' },
  { bg: '#cffafe', border: '#67e8f9', text: '#155e75' },
  { bg: '#fecdd3', border: '#fda4af', text: '#9f1239' },
];

const TableMode = ({ getAuthHeader, products, categories, cashierName, storeName, storeIvaRegime, onSaleCompleted }) => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCreateTable, setShowCreateTable] = useState(false);
  const [newTableName, setNewTableName] = useState('');
  const [editingTable, setEditingTable] = useState(null);
  const [editTableName, setEditTableName] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [processing, setProcessing] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  // Split billing
  const [assignTo, setAssignTo] = useState('');
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [newPersonName, setNewPersonName] = useState('');
  const [payMode, setPayMode] = useState('all'); // 'all' | 'person'
  const [payingPerson, setPayingPerson] = useState(null);
  const [localPeople, setLocalPeople] = useState([]); // people added manually before items
  const [lastTableSale, setLastTableSale] = useState(null); // for post-charge receipt
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [sendingToKitchen, setSendingToKitchen] = useState(false);
  const [tableInvoiceType, setTableInvoiceType] = useState('ticket');
  const [tableInvoiceCustomer, setTableInvoiceCustomer] = useState({ name: '', identification: '', id_type: 'FINAL_CONSUMER', email: '' });

  const loadTables = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tables`, { headers: getAuthHeader() });
      setTables(res.data);
      if (selectedTable) {
        const updated = res.data.find(t => t.id === selectedTable.id);
        if (updated) setSelectedTable(updated);
      }
    } catch { toast.error('Error al cargar mesas'); }
    finally { setLoading(false); }
  }, [getAuthHeader, selectedTable]);

  useEffect(() => { loadTables(); }, []);
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/tables`, { headers: getAuthHeader() });
        setTables(res.data);
        if (selectedTable) {
          const updated = res.data.find(t => t.id === selectedTable.id);
          if (updated) setSelectedTable(updated);
        }
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [getAuthHeader, selectedTable]);

  // Get unique people assigned to current table (from items + locally added)
  const tablePeople = useMemo(() => {
    const people = new Set();
    if (selectedTable?.items) {
      selectedTable.items.forEach(i => { if (i.assigned_to) people.add(i.assigned_to); });
    }
    localPeople.forEach(p => people.add(p));
    return Array.from(people);
  }, [selectedTable, localPeople]);

  const handleCreateTable = async () => {
    if (!newTableName.trim()) { toast.error('Ingresa un nombre'); return; }
    try {
      await axios.post(`${API}/tables`, { name: newTableName.trim() }, { headers: getAuthHeader() });
      toast.success('Mesa creada');
      setNewTableName('');
      setShowCreateTable(false);
      loadTables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleRenameTable = async () => {
    if (!editTableName.trim()) return;
    try {
      await axios.put(`${API}/tables/${editingTable.id}`, { name: editTableName.trim() }, { headers: getAuthHeader() });
      toast.success('Mesa renombrada');
      setEditingTable(null);
      loadTables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDeleteTable = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/tables/${deleteConfirm.id}`, { headers: getAuthHeader() });
      toast.success('Mesa eliminada');
      setDeleteConfirm(null);
      if (selectedTable?.id === deleteConfirm.id) setSelectedTable(null);
      loadTables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); setDeleteConfirm(null); }
  };

  const addProductToTable = async (product) => {
    if (!selectedTable) { toast.error('Selecciona una mesa primero'); return; }
    try {
      const res = await axios.post(`${API}/tables/${selectedTable.id}/add-items`, [{
        product_id: product.id, product_name: product.name, quantity: 1,
        unit_price: product.price, cost_price: product.cost_price || 0,
        unit_type: product.unit_type || 'unidad', assigned_to: assignTo || '',
        observation: '', category: product.category || '', tax_rate: product.tax_rate || 0
      }], { headers: getAuthHeader() });
      setSelectedTable(res.data);
      setTables(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      toast.success(`${product.name}${assignTo ? ` → ${assignTo}` : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleBarcodeScan = async (barcode) => {
    if (!selectedTable) { toast.error('Selecciona una mesa antes de escanear'); return; }
    try {
      const res = await axios.get(`${API}/products/barcode/${barcode}`, { headers: getAuthHeader() });
      await addProductToTable(res.data);
    } catch (err) {
      toast.error(err.response?.status === 404 ? `No encontrado: ${barcode}` : 'Error');
    }
  };

  const updateItemQty = async (lineId, productId, newQty, itemAssigned) => {
    if (!selectedTable) return;
    try {
      const res = await axios.put(`${API}/tables/${selectedTable.id}/update-item`, {
        product_id: productId, quantity: newQty, assigned_to: itemAssigned || '', line_id: lineId || ''
      }, { headers: getAuthHeader() });
      setSelectedTable(res.data);
      setTables(prev => prev.map(t => t.id === res.data.id ? res.data : t));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const removeItem = async (lineId, productId, itemAssigned) => {
    if (!selectedTable) return;
    try {
      let url = `${API}/tables/${selectedTable.id}/remove-item/${productId}`;
      const params = [];
      if (lineId) params.push(`line_id=${lineId}`);
      if (itemAssigned) params.push(`assigned_to=${encodeURIComponent(itemAssigned)}`);
      if (params.length) url += '?' + params.join('&');
      const res = await axios.delete(url, { headers: getAuthHeader() });
      setSelectedTable(res.data);
      setTables(prev => prev.map(t => t.id === res.data.id ? res.data : t));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updateObservation = (lineId, value) => {
    if (!selectedTable) return;
    setSelectedTable(prev => ({
      ...prev,
      items: prev.items.map(i => i.line_id === lineId ? { ...i, observation: value } : i)
    }));
  };

  const saveObservation = async (lineId, productId, assignedTo, observation) => {
    try {
      await axios.put(`${API}/tables/${selectedTable.id}/update-item`, {
        product_id: productId, quantity: selectedTable.items.find(i => i.line_id === lineId)?.quantity || 1,
        assigned_to: assignedTo || '', line_id: lineId, observation: observation
      }, { headers: getAuthHeader() });
    } catch {}
  };

  const sendToKitchen = async () => {
    if (!selectedTable?.items?.length) return;
    // Find items not yet sent to kitchen
    const unsent = selectedTable.items.filter(i => !i.sent_to_kitchen);
    if (unsent.length === 0) { toast.info('Todos los items ya fueron enviados a cocina'); return; }
    setSendingToKitchen(true);
    try {
      const res = await axios.post(`${API}/kitchen/send`, {
        table_id: selectedTable.id,
        table_name: selectedTable.name,
        items: unsent
      }, { headers: getAuthHeader() });
      toast.success(res.data.message);
      // Mark items locally as sent
      setSelectedTable(prev => ({
        ...prev,
        items: prev.items.map(i => ({ ...i, sent_to_kitchen: true }))
      }));
      loadTables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al enviar a cocina'); }
    finally { setSendingToKitchen(false); }
  };

  const handleCloseTable = async () => {
    if (!selectedTable) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/tables/${selectedTable.id}/close`, {
        payment_method: paymentMethod, cashier_name: cashierName
      }, { headers: getAuthHeader() });
      
      let accessKey = '';
      let facturaId = '';
      if (tableInvoiceType === 'invoice') {
        try {
          const invRes = await axios.post(`${API}/factuplan/invoice`, {
            sale_id: res.data.sale_id || '', items: res.data.items || [],
            total: res.data.total || 0, payment_method: paymentMethod,
            customer: { name: tableInvoiceCustomer.name || 'Consumidor Final', identification: tableInvoiceCustomer.identification || '9999999999999', id_type: tableInvoiceCustomer.id_type, email: tableInvoiceCustomer.email }
          }, { headers: getAuthHeader() });
          if (invRes.data.success) { accessKey = invRes.data.access_key || ''; facturaId = invRes.data.factuplan_id || ''; toast.success('Factura electrónica autorizada');
            if (tableInvoiceCustomer.identification && tableInvoiceCustomer.identification !== '9999999999999') {
              axios.post(`${API}/invoice-customers`, { identification: tableInvoiceCustomer.identification, name: tableInvoiceCustomer.name, email: tableInvoiceCustomer.email, id_type: tableInvoiceCustomer.id_type }, { headers: getAuthHeader() }).catch(() => {});
            }
          }
          else toast.error(invRes.data.message || 'Error en facturación');
        } catch { toast.error('Error al generar factura'); }
      }
      
      const saleData = { items: res.data.items, total: res.data.total, table_name: res.data.table_name || selectedTable.name, payment_method: paymentMethod, access_key: accessKey, factuplan_id: facturaId, invoice_type: tableInvoiceType, customer_name: tableInvoiceType === 'invoice' ? tableInvoiceCustomer.name : '', customer_id_number: tableInvoiceType === 'invoice' ? tableInvoiceCustomer.identification : '', customer_id_type: tableInvoiceType === 'invoice' ? tableInvoiceCustomer.id_type : '' };
      setLastTableSale(saleData);
      toast.success(`${res.data.message} - Total: $${res.data.total.toFixed(2)}`);
      setShowPayment(false);
      setSelectedTable(null);
      setAssignTo('');
      setLocalPeople([]);
      setTableInvoiceType('ticket');
      setTableInvoiceCustomer({ name: '', identification: '', id_type: 'FINAL_CONSUMER', email: '' });
      loadTables();
      if (onSaleCompleted) onSaleCompleted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const handleClosePersonBill = async () => {
    if (!selectedTable || payingPerson === null) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/tables/${selectedTable.id}/close-person`, {
        person_name: payingPerson, payment_method: paymentMethod, cashier_name: cashierName
      }, { headers: getAuthHeader() });
      
      let accessKey = '';
      let facturaId = '';
      if (tableInvoiceType === 'invoice') {
        try {
          const invRes = await axios.post(`${API}/factuplan/invoice`, {
            sale_id: res.data.sale_id || '', items: res.data.items || [],
            total: res.data.total || 0, payment_method: res.data.payment_method || paymentMethod,
            customer: { name: tableInvoiceCustomer.name || payingPerson || 'Consumidor Final', identification: tableInvoiceCustomer.identification || '9999999999999', id_type: tableInvoiceCustomer.id_type, email: tableInvoiceCustomer.email }
          }, { headers: getAuthHeader() });
          if (invRes.data.success) { accessKey = invRes.data.access_key || ''; facturaId = invRes.data.factuplan_id || ''; toast.success('Factura electrónica autorizada');
            if (tableInvoiceCustomer.identification && tableInvoiceCustomer.identification !== '9999999999999') {
              axios.post(`${API}/invoice-customers`, { identification: tableInvoiceCustomer.identification, name: tableInvoiceCustomer.name, email: tableInvoiceCustomer.email, id_type: tableInvoiceCustomer.id_type }, { headers: getAuthHeader() }).catch(() => {});
            }
          }
          else toast.error(invRes.data.message || 'Error en facturación');
        } catch { toast.error('Error al generar factura'); }
      }
      
      const saleData = { items: res.data.items, total: res.data.total, table_name: selectedTable.name, person: res.data.person || payingPerson, payment_method: res.data.payment_method || paymentMethod, access_key: accessKey, factuplan_id: facturaId, invoice_type: tableInvoiceType, customer_name: tableInvoiceType === 'invoice' ? tableInvoiceCustomer.name : '', customer_id_number: tableInvoiceType === 'invoice' ? tableInvoiceCustomer.identification : '', customer_id_type: tableInvoiceType === 'invoice' ? tableInvoiceCustomer.id_type : '' };
      setLastTableSale(saleData);
      toast.success(res.data.message);
      setSelectedTable(res.data.table);
      setTables(prev => prev.map(t => t.id === res.data.table.id ? res.data.table : t));
      setPayingPerson(null);
      setTableInvoiceType('ticket');
      setTableInvoiceCustomer({ name: '', identification: '', id_type: 'FINAL_CONSUMER', email: '' });
      if (!res.data.table.items?.length) {
        setShowPayment(false);
        setSelectedTable(null);
      }
      if (onSaleCompleted) onSaleCompleted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setProcessing(false); }
  };

  const addPerson = () => {
    if (!newPersonName.trim()) return;
    const name = newPersonName.trim();
    if (!localPeople.includes(name) && !tablePeople.includes(name)) {
      setLocalPeople(prev => [...prev, name]);
    }
    setAssignTo(name);
    setNewPersonName('');
    setShowAddPerson(false);
    toast.success(`Agregando para: ${name}`);
  };

  const PAYMENT_LABELS = { cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia' };

  const printTableReceipt = (saleData) => {
    const w = window.open('', '_blank', 'width=350,height=700');
    if (!w) { toast.error('Permite ventanas emergentes para imprimir'); return; }
    const items = saleData.items || [];
    const itemsHtml = items.map(i => `
      <tr>
        <td style="text-align:left;padding:4px 0">${escapeHtml(i.product_name)}</td>
        <td style="text-align:center;padding:4px 0">${Number(i.quantity) || 0}</td>
        <td style="text-align:right;padding:4px 0">$${(Number(i.unit_price) || 0).toFixed(2)}</td>
        <td style="text-align:right;padding:4px 0;font-weight:bold">$${(Number(i.subtotal) || 0).toFixed(2)}</td>
      </tr>`).join('');
    const date = new Date();
    const total = Number(saleData.total) || 0;
    const sName = escapeHtml(storeName || 'Tienda');
    const payLabel = PAYMENT_LABELS[saleData.payment_method] || saleData.payment_method || '';
    const isInvoice = saleData.invoice_type === 'invoice';
    const accessKey = saleData.access_key || '';
    const idTypeLabel = saleData.customer_id_type === 'RUC' ? 'RUC' : saleData.customer_id_type === 'CEDULA' ? 'Cédula' : 'ID';
    // IVA breakdown based on store's regime
    let subtotalBase0 = 0, subtotalBaseIVA = 0, totalIVA = 0;
    items.forEach(i => {
      const qty = Number(i.quantity) || 0;
      const pvp = Number(i.unit_price) || 0;
      if (storeIvaRegime === 'iva_15') {
        const base = pvp / 1.15;
        subtotalBaseIVA += base * qty;
        totalIVA += (pvp - base) * qty;
      } else {
        subtotalBase0 += pvp * qty;
      }
    });
    const subtotalSinIVA = subtotalBase0 + subtotalBaseIVA;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Recibo</title>
      <style>
        @page{margin:0;size:80mm auto}*{margin:0;padding:0;box-sizing:border-box}
        body{font-family:'Arial Black','Arial',sans-serif;width:76mm;margin:2mm auto;color:#000;font-size:14px;font-weight:500}
        .header{text-align:center;padding-bottom:6px}
        .store-name{font-size:22px;font-weight:900;letter-spacing:1px;margin-bottom:4px;text-transform:uppercase}
        .info{font-size:13px;color:#000;line-height:1.6;font-weight:600}
        .sep{border:none;border-top:2px dashed #000;margin:6px 0}
        .sep-thick{border:none;border-top:3px solid #000;margin:8px 0}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th{font-size:12px;font-weight:900;text-transform:uppercase;padding:4px 0;border-bottom:2px solid #000}
        td{font-weight:600}
        .total-line td{font-size:24px;font-weight:900;padding:6px 0}
        .pay-line{text-align:center;font-size:14px;font-weight:700;margin:6px 0;padding:4px 0;border:1px solid #000}
        .footer{text-align:center;font-size:12px;margin-top:8px;line-height:1.6;font-weight:600}
        .access-key{font-size:9px;word-break:break-all;text-align:center;font-weight:600;margin:4px 0;font-family:monospace;letter-spacing:0.5px}
        .sri-note{text-align:center;font-size:10px;font-weight:600;margin:4px 0}
      </style></head><body>
      <div class="header">
        <div class="store-name">${sName}</div>
        ${isInvoice ? '<div class="info" style="font-size:11px;font-weight:900;border:1px solid #000;padding:2px;margin:4px 0">FACTURA ELECTRONICA</div>' : ''}
        <div class="info">${date.toLocaleDateString('es-EC',{day:'2-digit',month:'short',year:'numeric'})} - ${date.toLocaleTimeString('es-EC',{hour:'2-digit',minute:'2-digit'})}</div>
        <div class="info">Mesa: ${escapeHtml(saleData.table_name || '')}</div>
        ${saleData.person ? `<div class="info">Cuenta: ${escapeHtml(saleData.person)}</div>` : ''}
        ${isInvoice && saleData.customer_name ? `<div class="info">Cliente: ${escapeHtml(saleData.customer_name)}</div>` : ''}
        ${isInvoice && saleData.customer_id_number ? `<div class="info">${idTypeLabel}: ${escapeHtml(saleData.customer_id_number)}</div>` : ''}
        ${!isInvoice ? '<div class="info" style="font-size:11px">CONSUMIDOR FINAL</div>' : ''}
        <div class="info">Cajero: ${escapeHtml(cashierName)}</div>
      </div>
      <hr class="sep">
      <table><thead><tr><th style="text-align:left">Producto</th><th style="text-align:center">Cant</th><th style="text-align:right">P.U.</th><th style="text-align:right">Subt.</th></tr></thead>
      <tbody>${itemsHtml}</tbody></table>
      <hr class="sep"><table>
        ${isInvoice ? `
          <tr><td>Subtotal (sin IVA):</td><td style="text-align:right">$${subtotalSinIVA.toFixed(2)}</td></tr>
          ${subtotalBase0 > 0 ? `<tr><td style="font-size:11px">Base IVA 0%:</td><td style="text-align:right;font-size:11px">$${subtotalBase0.toFixed(2)}</td></tr>` : ''}
          ${subtotalBaseIVA > 0 ? `<tr><td style="font-size:11px">Base IVA 15%:</td><td style="text-align:right;font-size:11px">$${subtotalBaseIVA.toFixed(2)}</td></tr>` : ''}
          <tr><td>IVA 15%:</td><td style="text-align:right">$${totalIVA.toFixed(2)}</td></tr>
        ` : `
          <tr><td>Subtotal:</td><td style="text-align:right">$${total.toFixed(2)}</td></tr>
        `}
      </table>
      <hr class="sep-thick">
      <table><tr class="total-line"><td>TOTAL</td><td style="text-align:right">$${total.toFixed(2)}</td></tr></table>
      <div class="pay-line">Pago: ${escapeHtml(payLabel)}</div>
      ${isInvoice && accessKey ? `<hr class="sep"><div class="sri-note">CLAVE DE ACCESO:</div><div class="access-key">${escapeHtml(accessKey)}</div><div class="sri-note">Consulte en: https://cel.sri.gob.ec/</div>` : ''}
      <hr class="sep">
      <div class="footer">Gracias por su compra<br><strong>${sName}</strong></div>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`);
    w.document.close();
  };

  const filteredProducts = useMemo(() => products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = !selectedCategory || p.category === selectedCategory;
    return matchSearch && matchCat;
  }), [products, searchTerm, selectedCategory]);

  const tableTotal = selectedTable?.items?.reduce((s, i) => s + (i.subtotal || 0), 0) || 0;

  // Group items by person
  const groupedItems = useMemo(() => {
    if (!selectedTable?.items) return {};
    const groups = {};
    selectedTable.items.forEach(item => {
      const person = item.assigned_to || '';
      if (!groups[person]) groups[person] = [];
      groups[person].push(item);
    });
    return groups;
  }, [selectedTable]);

  const personTotals = useMemo(() => {
    const totals = {};
    Object.entries(groupedItems).forEach(([person, items]) => {
      totals[person] = items.reduce((s, i) => s + (i.subtotal || 0), 0);
    });
    return totals;
  }, [groupedItems]);

  if (loading) return (
    <div className="flex items-center justify-center py-20"><UtensilsCrossed size={40} className="animate-spin text-accent-text" /></div>
  );

  const renderItemRow = (item) => {
    const isDrink = (item.category || '').toLowerCase().startsWith('bebida');
    return (
      <div key={item.line_id || `${item.product_id}-${Math.random()}`} className="p-3 rounded-lg border" style={{ borderColor: 'var(--border-light)' }}>
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1 pr-2 min-w-0">
            <p className="text-sm font-medium truncate">{item.product_name}</p>
            {isDrink && <span className="text-[8px] font-bold text-cyan-700 bg-cyan-50 px-1 py-0.5 rounded">BEBIDA</span>}
          </div>
          <button onClick={() => removeItem(item.line_id, item.product_id, item.assigned_to || '')} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={14} /></button>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button onClick={() => updateItemQty(item.line_id, item.product_id, item.quantity - 1, item.assigned_to || '')} className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-section" style={{ borderColor: 'var(--border-light)' }}><Minus size={14} /></button>
            <span className="w-8 text-center font-medium">{item.quantity}</span>
            <button onClick={() => updateItemQty(item.line_id, item.product_id, item.quantity + 1, item.assigned_to || '')} className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-section" style={{ borderColor: 'var(--border-light)' }}><Plus size={14} /></button>
          </div>
          <span className="font-bold text-accent-text">${(item.subtotal || 0).toFixed(2)}</span>
        </div>
        {/* Observation */}
        {!item.sent_to_kitchen && (
          <input type="text" placeholder="Observación (ej: sin cebolla)"
            value={item.observation || ''} 
            onChange={e => updateObservation(item.line_id, e.target.value)}
            onBlur={e => saveObservation(item.line_id, item.product_id, item.assigned_to, e.target.value)}
            className="w-full mt-2 px-2.5 py-1.5 text-xs rounded-lg border bg-gray-50 focus:bg-white focus:outline-none"
            style={{ borderColor: 'var(--border-light)' }} />
        )}
        {item.sent_to_kitchen && item.observation && (
          <p className="mt-1.5 text-xs italic text-amber-700 bg-amber-50 px-2 py-1 rounded">Nota: {item.observation}</p>
        )}
        {/* Status badges */}
        <div className="flex items-center gap-1.5 mt-1.5">
          {item.kitchen_ready 
            ? <span className="text-[9px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">LISTO</span>
            : item.sent_to_kitchen 
              ? <span className="text-[9px] font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">EN COCINA</span>
              : isDrink
                ? <span className="text-[9px] font-bold text-cyan-600 bg-cyan-50 px-2 py-0.5 rounded-full">BEBIDA - SIN COCINA</span>
                : <span className="text-[9px] text-text-muted bg-gray-100 px-2 py-0.5 rounded-full">Pendiente enviar</span>
          }
          {item.assigned_to && <span className="text-[9px] text-text-muted">{item.assigned_to}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 'calc(100vh - 160px)' }}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-1">
        {/* Left: Tables Grid + Products */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          {/* Tables Grid */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-sm flex items-center gap-2" data-testid="tables-header">
                <UtensilsCrossed size={18} className="text-accent-text" /> Mesas ({tables.length})
              </h3>
              <div className="flex gap-2">
                <button onClick={() => { if (!selectedTable) { toast.error('Selecciona una mesa'); return; } setShowScanner(true); }}
                  className="px-3 py-1.5 rounded-lg font-medium text-white text-xs" style={{ background: '#10b981' }} data-testid="table-scan-btn">
                  <Camera size={14} className="inline mr-1" /> Escanear
                </button>
                <button onClick={() => setShowCreateTable(true)} className="btn-primary text-xs px-3" data-testid="create-table-btn">
                  <Plus size={14} className="mr-1" /> Nueva Mesa
                </button>
              </div>
            </div>
            {tables.length === 0 ? (
              <div className="text-center py-8"><Coffee size={40} className="mx-auto text-text-muted mb-2" /><p className="text-text-muted text-sm">No hay mesas</p></div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                {(() => {
                  // Build waiter color map
                  const waiterMap = {};
                  let waiterIdx = 0;
                  tables.forEach(t => {
                    if (t.served_by && !waiterMap[t.served_by]) {
                      waiterMap[t.served_by] = WAITER_COLORS[waiterIdx % WAITER_COLORS.length];
                      waiterIdx++;
                    }
                  });
                  return tables.map(table => {
                    const baseStyle = STATUS_STYLES[table.status] || STATUS_STYLES.libre;
                    const waiterStyle = table.served_by && table.status === 'ocupada' ? waiterMap[table.served_by] : null;
                    const style = waiterStyle || baseStyle;
                    const isSelected = selectedTable?.id === table.id;
                    const people = new Set(); (table.items || []).forEach(i => { if (i.assigned_to) people.add(i.assigned_to); });
                    return (
                      <button key={table.id} onClick={() => { setSelectedTable(table); setAssignTo(''); setLocalPeople([]); }}
                        className="relative p-3 rounded-xl border-2 transition-all text-center"
                        style={{ background: style.bg, borderColor: isSelected ? 'var(--accent-primary)' : style.border,
                          boxShadow: isSelected ? '0 0 0 2px var(--accent-primary)' : 'none', transform: isSelected ? 'scale(1.05)' : 'scale(1)' }}
                        data-testid={`table-${table.id}`}>
                        <p className="font-bold text-xs truncate" style={{ color: style.text }}>{table.name}</p>
                        <p className="text-[10px] mt-0.5" style={{ color: style.text, opacity: 0.8 }}>{baseStyle.label}</p>
                        {table.status === 'ocupada' && table.total > 0 && (
                          <p className="font-bold text-xs mt-1" style={{ color: style.text }}>${table.total.toFixed(2)}</p>
                        )}
                        {table.served_by && table.status === 'ocupada' && (
                          <p className="text-[9px] mt-0.5 font-semibold truncate" style={{ color: style.text }}><User size={9} className="inline" /> {table.served_by}</p>
                        )}
                        {people.size > 0 && <p className="text-[9px] mt-0.5" style={{ color: style.text }}><Users size={9} className="inline" /> {people.size}</p>}
                        <div className="absolute -top-1 -right-1 flex gap-0.5">
                          <button onClick={e => { e.stopPropagation(); setEditingTable(table); setEditTableName(table.name); }}
                            className="w-5 h-5 rounded-full bg-white shadow flex items-center justify-center"><Edit size={10} className="text-gray-500" /></button>
                          {table.status === 'libre' && (
                            <button onClick={e => { e.stopPropagation(); setDeleteConfirm(table); }}
                              className="w-5 h-5 rounded-full bg-white shadow flex items-center justify-center"><Trash2 size={10} className="text-red-400" /></button>
                          )}
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            )}
          </div>

          {/* Person selector + Products Grid */}
          {selectedTable && (
            <>
              {/* Person Selector Bar */}
              <div className="bg-white rounded-xl border p-3" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                  <span className="text-xs font-medium text-text-muted flex-shrink-0">Para:</span>
                  <button onClick={() => setAssignTo('')}
                    className="px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition-all"
                    style={{ background: !assignTo ? 'var(--accent-primary)' : '#f3f4f6', color: !assignTo ? 'white' : '#666' }}
                    data-testid="assign-general">
                    Todos
                  </button>
                  {tablePeople.map((person, i) => (
                    <button key={person} onClick={() => setAssignTo(person)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition-all flex items-center gap-1"
                      style={{ background: assignTo === person ? PERSON_COLORS[i % PERSON_COLORS.length] : '#f3f4f6', 
                              color: assignTo === person ? 'white' : '#666' }}
                      data-testid={`assign-${person}`}>
                      <User size={11} /> {person}
                    </button>
                  ))}
                  <button onClick={() => setShowAddPerson(true)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium flex-shrink-0 border border-dashed flex items-center gap-1 hover:bg-gray-50"
                    style={{ borderColor: '#d1d5db', color: '#666' }}
                    data-testid="add-person-btn">
                    <UserPlus size={12} /> Persona
                  </button>
                </div>
              </div>

              {/* Products */}
              <div className="bg-white rounded-xl border overflow-hidden flex-1 flex flex-col" style={{ borderColor: 'var(--border-light)' }}>
                <div className="p-3 border-b" style={{ borderColor: 'var(--border-light)' }}>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                      <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar..." className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} data-testid="table-product-search" />
                    </div>
                    <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}>
                      <option value="">Todas</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    {filteredProducts.map(product => (
                      <button key={product.id} onClick={() => addProductToTable(product)}
                        className="bg-white border rounded-lg p-2 text-left hover:shadow-md transition-all" style={{ borderColor: 'var(--border-light)' }}
                        data-testid={`table-product-${product.id}`}>
                        <img src={product.image} alt={product.name} className="w-full h-16 object-cover rounded mb-1" />
                        <p className="font-medium text-xs truncate">{product.name}</p>
                        <span className="font-bold text-xs" style={{ color: 'var(--accent-text)' }}>${product.price.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right: Cart Panel - hidden on mobile, visible on lg+ */}
        <div className="hidden lg:flex bg-white rounded-xl border overflow-hidden flex-col" style={{ borderColor: 'var(--border-light)' }}>
          {selectedTable ? (
            <>
              <div className="p-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex items-center gap-2">
                  <ShoppingCart className="text-accent-text" size={20} />
                  <h2 className="font-bold" data-testid="selected-table-name">{selectedTable.name}</h2>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: STATUS_STYLES[selectedTable.status]?.bg, color: STATUS_STYLES[selectedTable.status]?.text }}>{STATUS_STYLES[selectedTable.status]?.label}</span>
                  <span className="ml-auto bg-accent-wash text-accent-text px-2 py-0.5 rounded-full text-sm">{selectedTable.items?.length || 0}</span>
                  <button onClick={() => { setSelectedTable(null); setAssignTo(''); setLocalPeople([]); }} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                {(!selectedTable.items || selectedTable.items.length === 0) ? (
                  <div className="text-center py-8 text-text-muted">
                    <ShoppingCart size={40} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Mesa vacía</p>
                  </div>
                ) : tablePeople.length === 0 ? (
                  <div className="space-y-2">{selectedTable.items.map(renderItemRow)}</div>
                ) : (
                  <div className="space-y-4">
                    {groupedItems[''] && groupedItems[''].length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-text-muted mb-2 flex items-center gap-1"><Users size={12} /> General</p>
                        <div className="space-y-2">{groupedItems[''].map(renderItemRow)}</div>
                      </div>
                    )}
                    {tablePeople.map((person, i) => (
                      <div key={person}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-bold flex items-center gap-1" style={{ color: PERSON_COLORS[i % PERSON_COLORS.length] }}>
                            <User size={12} /> {person}
                          </p>
                          <span className="text-xs font-bold" style={{ color: PERSON_COLORS[i % PERSON_COLORS.length] }}>
                            ${(personTotals[person] || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="space-y-2">{(groupedItems[person] || []).map(renderItemRow)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer: identical layout to POS cart footer */}
              {selectedTable.items && selectedTable.items.length > 0 && (
                <div className="p-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                  {selectedTable.items.some(i => !i.sent_to_kitchen) && (
                    <p className="text-xs text-amber-600 font-medium mb-2 text-center">
                      {selectedTable.items.filter(i => !i.sent_to_kitchen).length} item(s) sin enviar a cocina
                    </p>
                  )}
                  <div className="flex justify-between items-center mb-4 p-3 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <span className="font-medium">TOTAL</span>
                    <span className="text-2xl font-bold text-accent-text" data-testid="table-total">${tableTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={sendToKitchen} disabled={sendingToKitchen || !selectedTable.items.some(i => !i.sent_to_kitchen)}
                      className="flex-1 py-3 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-1 disabled:opacity-40 btn-secondary"
                      data-testid="send-kitchen-btn">
                      <Coffee size={18} /> {sendingToKitchen ? '...' : 'Enviar a Cocina'}
                    </button>
                    <button onClick={() => { setShowPayment(true); setPayMode('all'); setPayingPerson(null); }}
                      className="flex-1 py-3 rounded-lg text-white font-bold text-sm transition-all flex items-center justify-center gap-1 btn-primary"
                      data-testid="table-cobrar-btn">
                      <Check size={18} /> Cobrar
                    </button>
                  </div>
                  {lastTableSale && (
                    <button onClick={() => printTableReceipt(lastTableSale)}
                      className="w-full mt-2 py-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                      style={{ borderColor: 'var(--border-light)' }}>
                      <Printer size={16} />Reimprimir Último Recibo
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center text-text-muted">
                <ShoppingCart size={40} className="mx-auto mb-2 opacity-50" />
                <p className="font-medium">Selecciona una mesa</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Table Modal */}
      {showCreateTable && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowCreateTable(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6" data-testid="create-table-modal">
              <h3 className="font-bold text-lg mb-4">Nueva Mesa</h3>
              <input type="text" value={newTableName} onChange={e => setNewTableName(e.target.value)} placeholder="Ej: Mesa 1, Terraza 2..."
                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 mb-4" style={{ borderColor: 'var(--border-light)' }} autoFocus
                onKeyDown={e => e.key === 'Enter' && handleCreateTable()} data-testid="new-table-name-input" />
              <div className="flex gap-3">
                <button onClick={handleCreateTable} className="btn-primary flex-1"><Plus size={18} className="mr-2" /> Crear</button>
                <button onClick={() => setShowCreateTable(false)} className="btn-secondary">Cancelar</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Rename Table Modal */}
      {editingTable && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setEditingTable(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
              <h3 className="font-bold text-lg mb-4">Renombrar Mesa</h3>
              <input type="text" value={editTableName} onChange={e => setEditTableName(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 mb-4" style={{ borderColor: 'var(--border-light)' }} autoFocus
                onKeyDown={e => e.key === 'Enter' && handleRenameTable()} />
              <div className="flex gap-3"><button onClick={handleRenameTable} className="btn-primary flex-1"><Save size={18} className="mr-2" /> Guardar</button><button onClick={() => setEditingTable(null)} className="btn-secondary">Cancelar</button></div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setDeleteConfirm(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full text-center">
              <Trash2 size={40} className="mx-auto text-red-500 mb-3" />
              <h3 className="font-bold text-lg">Eliminar {deleteConfirm.name}?</h3>
              <div className="flex gap-3 mt-4"><button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1">Cancelar</button><button onClick={handleDeleteTable} className="flex-1 px-4 py-2 rounded-lg text-white font-medium" style={{ background: '#ef4444' }}>Eliminar</button></div>
            </div>
          </div>
        </>
      )}

      {/* Add Person Modal */}
      {showAddPerson && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowAddPerson(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6" data-testid="add-person-modal">
              <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><UserPlus size={20} className="text-accent-text" /> Nueva Persona</h3>
              <p className="text-xs text-text-muted mb-4">Los productos que agregues irán a la cuenta de esta persona</p>
              <input type="text" value={newPersonName} onChange={e => setNewPersonName(e.target.value)} placeholder="Nombre (ej: Juan, Maria...)"
                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 mb-4" style={{ borderColor: 'var(--border-light)' }} autoFocus
                onKeyDown={e => e.key === 'Enter' && addPerson()} data-testid="new-person-name" />
              <div className="flex gap-3"><button onClick={addPerson} className="btn-primary flex-1"><UserPlus size={16} className="mr-2" /> Agregar</button><button onClick={() => setShowAddPerson(false)} className="btn-secondary">Cancelar</button></div>
            </div>
          </div>
        </>
      )}

      {/* Payment Modal */}
      {showPayment && selectedTable && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowPayment(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" data-testid="table-payment-modal">
              <div className="p-6 border-b" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="font-bold text-lg">Cobrar {selectedTable.name}</h3>
                {tablePeople.length > 0 && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => { setPayMode('all'); setPayingPerson(null); }}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                      style={{ background: payMode === 'all' ? 'var(--accent-primary)' : '#f3f4f6', color: payMode === 'all' ? 'white' : '#666' }}
                      data-testid="pay-all-btn">
                      Cobrar Todo (${tableTotal.toFixed(2)})
                    </button>
                    <button onClick={() => setPayMode('person')}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                      style={{ background: payMode === 'person' ? 'var(--accent-primary)' : '#f3f4f6', color: payMode === 'person' ? 'white' : '#666' }}
                      data-testid="pay-person-btn">
                      <Users size={14} className="inline mr-1" /> Por Persona
                    </button>
                  </div>
                )}
              </div>
              <div className="p-6">
                {payMode === 'person' && tablePeople.length > 0 ? (
                  <div className="space-y-3 mb-4">
                    <p className="text-sm font-medium">Selecciona quién paga:</p>
                    {/* General items */}
                    {groupedItems[''] && groupedItems[''].length > 0 && (
                      <button onClick={() => setPayingPerson('')}
                        className="w-full p-3 rounded-lg border text-left transition-all"
                        style={{ borderColor: payingPerson === '' ? 'var(--accent-primary)' : 'var(--border-light)', background: payingPerson === '' ? 'var(--accent-wash)' : 'white' }}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm flex items-center gap-2"><Users size={14} /> General</span>
                          <span className="font-bold text-accent-text">${(personTotals[''] || 0).toFixed(2)}</span>
                        </div>
                        <div className="mt-2 space-y-1">
                          {groupedItems[''].map(item => (
                            <div key={item.product_id} className="flex justify-between text-xs text-text-muted">
                              <span>{item.product_name} x{item.quantity}</span>
                              <span className="font-medium">${(item.subtotal || 0).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </button>
                    )}
                    {tablePeople.map((person, i) => (
                      <button key={person} onClick={() => setPayingPerson(person)}
                        className="w-full p-3 rounded-lg border text-left transition-all"
                        style={{ borderColor: payingPerson === person ? PERSON_COLORS[i % PERSON_COLORS.length] : 'var(--border-light)',
                                background: payingPerson === person ? `${PERSON_COLORS[i % PERSON_COLORS.length]}10` : 'white' }}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm flex items-center gap-2" style={{ color: PERSON_COLORS[i % PERSON_COLORS.length] }}><User size={14} /> {person}</span>
                          <span className="font-bold" style={{ color: PERSON_COLORS[i % PERSON_COLORS.length] }}>${(personTotals[person] || 0).toFixed(2)}</span>
                        </div>
                        <div className="mt-2 space-y-1">
                          {(groupedItems[person] || []).map(item => (
                            <div key={item.product_id} className="flex justify-between text-xs text-text-muted">
                              <span>{item.product_name} x{item.quantity}</span>
                              <span className="font-medium">${(item.subtotal || 0).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="max-h-40 overflow-y-auto mb-4 space-y-1">
                    {/* Group items by product for clean billing */}
                    {(() => {
                      const grouped = {};
                      (selectedTable.items || []).forEach(item => {
                        const key = item.product_id;
                        if (grouped[key]) {
                          grouped[key].quantity += item.quantity;
                          grouped[key].subtotal += (item.subtotal || 0);
                        } else {
                          grouped[key] = { ...item, quantity: item.quantity, subtotal: item.subtotal || 0 };
                        }
                      });
                      return Object.values(grouped).map(item => (
                        <div key={item.product_id} className="flex justify-between text-sm">
                          <span className="truncate flex-1">{item.product_name} x{item.quantity}</span>
                          <span className="font-medium ml-2">${item.subtotal.toFixed(2)}</span>
                        </div>
                      ));
                    })()}
                  </div>
                )}

                <div className="border-t pt-4 mb-4" style={{ borderColor: 'var(--border-light)' }}>
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg">{payMode === 'person' && payingPerson !== null ? `Cuenta: ${payingPerson || 'General'}` : 'Total'}</span>
                    <span className="font-bold text-2xl" style={{ color: 'var(--accent-text)' }}>
                      ${payMode === 'person' && payingPerson !== null ? (personTotals[payingPerson] || 0).toFixed(2) : tableTotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                <p className="text-sm font-medium mb-2">Tipo de comprobante</p>
                <div className="flex rounded-lg border overflow-hidden mb-4" style={{ borderColor: 'var(--border-light)' }}>
                  <button onClick={() => setTableInvoiceType('ticket')}
                    className="flex-1 py-2.5 text-xs font-medium transition-all flex items-center justify-center gap-1"
                    style={{ background: tableInvoiceType === 'ticket' ? 'var(--accent-primary)' : 'white', color: tableInvoiceType === 'ticket' ? 'white' : 'var(--text-muted)' }}
                    data-testid="table-invoice-ticket">
                    <Printer size={14} /> Solo Ticket
                  </button>
                  <button onClick={() => setTableInvoiceType('invoice')}
                    className="flex-1 py-2.5 text-xs font-medium transition-all flex items-center justify-center gap-1"
                    style={{ background: tableInvoiceType === 'invoice' ? '#16a34a' : 'white', color: tableInvoiceType === 'invoice' ? 'white' : 'var(--text-muted)' }}
                    data-testid="table-invoice-electronic">
                    <FileText size={14} /> Factura Electrónica
                  </button>
                </div>
                {tableInvoiceType === 'invoice' && (
                  <div className="mb-4 p-3 rounded-lg border space-y-2" style={{ borderColor: '#16a34a40', background: '#f0fdf4' }}>
                    <p className="text-xs font-bold text-green-800">Datos del cliente:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={tableInvoiceCustomer.id_type} onChange={e => setTableInvoiceCustomer({...tableInvoiceCustomer, id_type: e.target.value})}
                        className="px-2 py-1.5 rounded border text-xs" style={{ borderColor: 'var(--border-light)' }}>
                        <option value="FINAL_CONSUMER">Consumidor Final</option>
                        <option value="CEDULA">Cédula</option>
                        <option value="RUC">RUC</option>
                        <option value="PASSPORT">Pasaporte</option>
                      </select>
                      <input type="text" placeholder="# Identificación" value={tableInvoiceCustomer.identification}
                        onChange={e => {
                          const val = e.target.value;
                          setTableInvoiceCustomer(prev => ({...prev, identification: val}));
                          if (val.length >= 10 && (val.length === 10 || val.length === 13)) {
                            axios.get(`${API}/invoice-customers/search?identification=${val}`, { headers: getAuthHeader() })
                              .then(r => { if (r.data && r.data.name) setTableInvoiceCustomer(prev => ({...prev, name: r.data.name, email: r.data.email || prev.email, id_type: r.data.id_type || prev.id_type})); })
                              .catch(() => {});
                          }
                        }}
                        className="px-2 py-1.5 rounded border text-xs" style={{ borderColor: 'var(--border-light)' }} />
                    </div>
                    <input type="text" placeholder="Nombre / Razón Social" value={tableInvoiceCustomer.name}
                      onChange={e => setTableInvoiceCustomer({...tableInvoiceCustomer, name: e.target.value})}
                      className="w-full px-2 py-1.5 rounded border text-xs" style={{ borderColor: 'var(--border-light)' }} />
                    <input type="email" placeholder="Email (para enviar factura)" value={tableInvoiceCustomer.email}
                      onChange={e => setTableInvoiceCustomer({...tableInvoiceCustomer, email: e.target.value})}
                      className="w-full px-2 py-1.5 rounded border text-xs" style={{ borderColor: 'var(--border-light)' }} />
                  </div>
                )}

                <p className="text-sm font-medium mb-2">Método de pago</p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[{ key: 'cash', icon: Banknote, label: 'Efectivo' }, { key: 'card', icon: CreditCard, label: 'Tarjeta' }, { key: 'transfer', icon: DollarSign, label: 'Transfer.' }].map(m => (
                    <button key={m.key} onClick={() => setPaymentMethod(m.key)}
                      className="p-3 rounded-lg border text-center transition-all text-sm"
                      style={{ borderColor: paymentMethod === m.key ? 'var(--accent-primary)' : 'var(--border-light)', background: paymentMethod === m.key ? 'var(--accent-wash)' : 'white', fontWeight: paymentMethod === m.key ? 600 : 400 }}
                      data-testid={`table-pay-${m.key}`}>
                      <m.icon size={20} className="mx-auto mb-1" style={{ color: paymentMethod === m.key ? 'var(--accent-text)' : 'var(--text-muted)' }} />{m.label}
                    </button>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button onClick={payMode === 'person' && payingPerson !== null ? handleClosePersonBill : handleCloseTable}
                    disabled={processing || (payMode === 'person' && payingPerson === null)}
                    className="flex-1 py-3 rounded-xl text-white font-bold transition-all flex items-center justify-center gap-2"
                    style={{ background: 'var(--accent-primary)', opacity: (processing || (payMode === 'person' && payingPerson === null)) ? 0.6 : 1 }}
                    data-testid="table-confirm-pay-btn">
                    <Check size={20} />
                    {processing ? 'Procesando...' : payMode === 'person' && payingPerson !== null ? `Cobrar a ${payingPerson || 'General'}` : 'Cobrar Todo'}
                  </button>
                  <button onClick={() => setShowPayment(false)} className="btn-secondary">Cancelar</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Post-charge Receipt Modal */}
      {lastTableSale && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setLastTableSale(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center" data-testid="table-receipt-modal">
              <Check size={48} className="mx-auto mb-3" style={{ color: 'var(--accent-text)' }} />
              <h3 className="font-bold text-lg mb-1">Cobro Exitoso</h3>
              {lastTableSale.person && <p className="text-sm text-text-muted mb-1">Cuenta: {lastTableSale.person}</p>}
              <p className="text-sm text-text-muted mb-1">Mesa: {lastTableSale.table_name}</p>
              <p className="text-3xl font-bold mt-2 mb-4" style={{ color: 'var(--accent-text)' }}>${(lastTableSale.total || 0).toFixed(2)}</p>
              {lastTableSale.invoice_type === 'invoice' && lastTableSale.access_key && (
                <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-left">
                  <p className="text-xs font-bold text-green-800 mb-1">FACTURA ELECTRONICA AUTORIZADA</p>
                  <p className="text-xs text-green-700 font-mono break-all">{lastTableSale.access_key}</p>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => { printTableReceipt(lastTableSale); }}
                  className="flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 border-2"
                  style={{ borderColor: 'var(--accent-primary)', color: 'var(--accent-text)' }}
                  data-testid="table-print-receipt-btn">
                  <Printer size={18} /> Imprimir
                </button>
                {lastTableSale.factuplan_id && (
                  <button onClick={() => window.open(`${API}/factuplan/receipt/${lastTableSale.factuplan_id}/pdf`, '_blank')}
                    className="py-3 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 border-2 border-green-300 text-green-700 hover:bg-green-50"
                    data-testid="table-download-ride-btn">
                    <FileText size={18} /> RIDE
                  </button>
                )}
                <button onClick={() => setLastTableSale(null)}
                  className="flex-1 py-3 rounded-xl text-white font-bold text-sm"
                  style={{ background: 'var(--accent-primary)' }}
                  data-testid="table-close-receipt-btn">
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {showScanner && <BarcodeScanner onScan={handleBarcodeScan} onClose={() => setShowScanner(false)} />}

      {/* Mobile Floating Cart Button - same as POS */}
      {selectedTable && (
        <button
          onClick={() => setMobileCartOpen(true)}
          className="lg:hidden fixed bottom-4 right-4 z-30 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white"
          style={{ background: 'var(--accent-primary)' }}
          data-testid="mobile-table-cart-btn">
          <ShoppingCart size={24} />
          {selectedTable.items?.length > 0 && (
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">{selectedTable.items.length}</span>
          )}
        </button>
      )}

      {/* Mobile Cart Slide-up Panel - same layout as POS */}
      {mobileCartOpen && selectedTable && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileCartOpen(false)} />
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl" style={{ maxHeight: '85vh' }}>
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
              <div className="flex items-center gap-2">
                <ShoppingCart className="text-accent-text" size={20} />
                <h2 className="font-bold">{selectedTable.name}</h2>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: STATUS_STYLES[selectedTable.status]?.bg, color: STATUS_STYLES[selectedTable.status]?.text }}>{STATUS_STYLES[selectedTable.status]?.label}</span>
                <span className="bg-accent-wash text-accent-text px-2 py-0.5 rounded-full text-sm">{selectedTable.items?.length || 0}</span>
              </div>
              <button onClick={() => setMobileCartOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="overflow-y-auto p-3" style={{ maxHeight: '40vh' }}>
              {(!selectedTable.items || selectedTable.items.length === 0) ? (
                <div className="text-center py-6 text-text-muted">
                  <ShoppingCart size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Mesa vacía</p>
                </div>
              ) : tablePeople.length === 0 ? (
                <div className="space-y-2">{selectedTable.items.map(renderItemRow)}</div>
              ) : (
                <div className="space-y-3">
                  {groupedItems[''] && groupedItems[''].length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-text-muted mb-2"><Users size={12} className="inline mr-1" />General</p>
                      <div className="space-y-2">{groupedItems[''].map(renderItemRow)}</div>
                    </div>
                  )}
                  {tablePeople.map((person, i) => (
                    <div key={person}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold" style={{ color: PERSON_COLORS[i % PERSON_COLORS.length] }}>
                          <User size={12} className="inline mr-1" />{person}
                        </p>
                        <span className="text-xs font-bold" style={{ color: PERSON_COLORS[i % PERSON_COLORS.length] }}>${(personTotals[person] || 0).toFixed(2)}</span>
                      </div>
                      <div className="space-y-2">{(groupedItems[person] || []).map(renderItemRow)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedTable.items && selectedTable.items.length > 0 && (
              <div className="p-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                {selectedTable.items.some(i => !i.sent_to_kitchen) && (
                  <p className="text-xs text-amber-600 font-medium mb-2 text-center">
                    {selectedTable.items.filter(i => !i.sent_to_kitchen).length} item(s) sin enviar a cocina
                  </p>
                )}
                <div className="flex justify-between items-center mb-3 p-3 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                  <span className="font-medium">TOTAL</span>
                  <span className="text-2xl font-bold text-accent-text">${tableTotal.toFixed(2)}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setMobileCartOpen(false); sendToKitchen(); }} disabled={sendingToKitchen || !selectedTable.items.some(i => !i.sent_to_kitchen)}
                    className="flex-1 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-40 btn-secondary">
                    <Coffee size={16} /> Cocina
                  </button>
                  <button onClick={() => { setMobileCartOpen(false); setShowPayment(true); setPayMode('all'); setPayingPerson(null); }}
                    className="flex-1 py-3 rounded-lg text-white font-bold text-sm flex items-center justify-center gap-1 btn-primary">
                    <Check size={16} /> Cobrar
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TableMode;
